import { ServerUser } from "./User";

export interface ExhibitionSessionResponse {
  token: string;
  user: ServerUser;
  sessionId: number;
  designId: number;
  /** The first clone of the showcase: the design the guided tour describes. */
  demoDesignId: number;
  showcaseDesignId: number;
  expiresAt: string;
  resumed: boolean;
}

export interface ExhibitionSessionState {
  sessionId: number;
  currentDesignId: number;
  /**
   * The demonstration design this session was issued: the copy of the
   * configured showcase that was cloned at login.
   *
   * Distinct from currentDesignId, which follows the visitor as they create or
   * open other designs. The guided tour belongs to this design alone — its
   * pressure and WBCT reveals only have anything to show here — so it is set
   * once and never moved.
   */
  demoDesignId: number;
  showcaseDesignId: number;
  expiresAt: string;
  resumed: boolean;
  /**
   * The tour has run to the end at least once.
   *
   * Reveals the Replay and Skip controls. They stay hidden until then, because
   * before the tour has finished there is nothing to replay and the visitor is
   * being spoken to rather than asked to choose.
   */
  tourCompleted: boolean;
  /**
   * The visitor pressed Skip.
   *
   * The only thing that suppresses the tour. Opening the demonstration design
   * otherwise starts it, so a reload replays it rather than leaving the visitor
   * with a silent app; skipping is the one way to say no to that.
   */
  tourDismissed: boolean;
  microphoneGranted: boolean;
}

export interface ExhibitionAdminSession {
  sessionId: number;
  eventKey: string;
  guestAlias: string;
  maskedEmail: string;
  userId: number;
  showcaseDesignId: number;
  currentDesignId: number | null;
  createdAt: string;
  lastActiveAt: string;
  expiresAt: string;
  status: "Active" | "Expired" | string;
}

export const EXHIBITION_STORAGE_KEY = "exhibitionSession";

export function isExhibitionSessionValid(
  session: Pick<ExhibitionSessionState, "sessionId" | "expiresAt"> | null
): boolean {
  if (!session?.sessionId || !session.expiresAt) return false;
  const expiresAt = Date.parse(session.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}
